/// <reference path="../../ui/types.d.ts" />
import { FC, Fragment, useEffect, useRef } from 'react';

interface ConfigField {
    key?: string;
    label?: string;
    labelHtml?: string;
    helpText?: string;
    helpTextHtml?: string;
    type?: string;
    defaultValue?: any;
    dependencySettings?: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    options?: Array<{ value: string; label: string }>;
    noticeType?: string;
    noIcon?: boolean;
}

interface ConfigurationTab {
    name: string;
    fields: ConfigField[];
}

interface Notice {
    title: string;
    content: string;
    type?: string;
    icon?: boolean;
    close?: boolean;
}

interface InfoModal {
    title: string;
    content: string;
}

interface MenuItem {
    label: string;
    action: string;
    title?: string;
}

interface MainPageProps {
    name: string;
    userId?: string;
    notice?: Notice;
    checkSubscription?: boolean;
    isManager?: boolean;
    isOwner?: boolean;
    infoModal?: InfoModal;
    configurationFields?: ConfigurationTab[];
    config?: string; // JSON string
    syncNewElements?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    withCronSync?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    webhooks?: {
        crowdin?: boolean;
        integration?: boolean;
    };
    integrationOneLevelFetching?: boolean;
    integrationSearchListener?: boolean;
    integrationPagination?: boolean;
    uploadTranslations?: boolean;
    excludedTargetLanguages?: boolean;
    forcePushSources?: boolean;
    forcePushTranslations?: boolean;
    filtering?: {
        crowdinLanguages?: boolean;
        integrationFilterConfig?: string;
    };
    hasOrganization?: boolean;
    zenModeUrl?: string;
    asyncProgress?: {
        checkInterval: number;
    };
    reloadOnConfigSave?: boolean;
    progressiveCrowdinFilesLoading?: boolean;
    integrationButtonMenuItems?: MenuItem[];
    crowdinButtonMenuItems?: MenuItem[];
}

const renderConfigField = (field: ConfigField, settingsFieldChange: (e: any) => void, suffix: string = 'settings') => {
    if (!field.key) {
        return field.labelHtml ? (
            <crowdin-p
                {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                dangerouslySetInnerHTML={{ __html: field.labelHtml }}
            />
        ) : (
            <crowdin-p {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}>
                {field.label}
            </crowdin-p>
        );
    }

    const commonProps = {
        id: `${field.key}-${suffix}`,
        _key: field.key,
        label: field.label,
        ...(field.helpText ? { 'help-text': field.helpText } : {}),
        ...(field.helpTextHtml ? { 'help-text-html': field.helpTextHtml } : {}),
        ...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {}),
    };

    switch (field.type) {
        case 'checkbox':
            return (
                <crowdin-checkbox
                    {...commonProps}
                    value="false"
                    use-switch
                    {...(field.defaultValue === true ? { checked: String(field.defaultValue) } : {})}
                    onChange={settingsFieldChange}
                />
            );

        case 'select':
            return (
                <crowdin-select
                    {...commonProps}
                    {...(field.isMulti ? { 'is-multi': true, 'close-on-select': 'false' } : {})}
                    {...(field.isSearchable ? { 'is-searchable': true } : {})}
                    is-position-fixed
                    onChange={settingsFieldChange}
                >
                    {field.options?.map((option) => {
                        const isSelected = Array.isArray(field.defaultValue)
                            ? field.defaultValue.includes(option.value)
                            : field.defaultValue === option.value;
                        return (
                            <option key={option.value} value={option.value} {...(isSelected ? { selected: true } : {})}>
                                {option.label}
                            </option>
                        );
                    })}
                </crowdin-select>
            );

        case 'text':
            return (
                <crowdin-input
                    {...commonProps}
                    with-fixed-height
                    value={field.defaultValue || ''}
                    onChange={settingsFieldChange}
                />
            );

        case 'textarea':
            return (
                <crowdin-textarea {...commonProps} value={field.defaultValue || ''} onChange={settingsFieldChange} />
            );

        case 'notice':
            return (
                <crowdin-alert
                    {...(field.noticeType ? { type: field.noticeType } : {})}
                    {...(field.label ? { title: field.label } : {})}
                    {...(field.noIcon ? { 'no-icon': String(field.noIcon) } : {})}
                    {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                    dangerouslySetInnerHTML={{ __html: field.helpTextHtml || field.helpText || '' }}
                />
            );

        default:
            return null;
    }
};

export const MainPage: FC<MainPageProps> = (props) => {
    const {
        name,
        userId,
        notice,
        checkSubscription,
        isManager,
        isOwner,
        infoModal,
        configurationFields,
        config: configString,
        syncNewElements,
        withCronSync,
        webhooks,
        integrationOneLevelFetching,
        integrationSearchListener,
        integrationPagination,
        excludedTargetLanguages,
        filtering,
        hasOrganization,
        zenModeUrl,
        asyncProgress = { checkInterval: 3000 },
        reloadOnConfigSave,
        progressiveCrowdinFilesLoading,
        integrationButtonMenuItems,
        crowdinButtonMenuItems,
    } = props;

    const config = useRef<any>(configString ? JSON.parse(configString.replace(/\\n/g, '\\\\n')) : {});

    const folderType = '0';
    const fileType = '1';
    const branchType = '2';

    const JOB_TYPE = {
        updateCrowdin: 'updateCrowdin',
        updateIntegration: 'updateIntegration',
        updateTargetLanguages: 'updateTargetLanguages',
        integrationSyncSettingsSave: 'integrationSyncSettingsSave',
        crowdinSyncSettingsSave: 'crowdinSyncSettingsSave',
        integrationSettingsSave: 'integrationSettingsSave',
    };

    const JOB_STATUS = {
        created: 'created',
        inProgress: 'inProgress',
        failed: 'failed',
        canceled: 'canceled',
        finished: 'finished',
    };

    const silentJobs = [JOB_TYPE.integrationSettingsSave];

    const appComponent = useRef<any>(null);
    const subscriptionModal = useRef<any>(null);
    const languageModal = useRef<any>(null);
    const usersSelectRef = useRef<HTMLElement>(null);

    const crowdinFilesToUpdateTargetLanguages = useRef<any>([]);
    const asyncJobs = useRef<any>({});
    const project = useRef<any>({});
    const crowdinData = useRef<any[]>([]);
    const fileToSync = useRef<any[]>([]);
    const syncData = useRef<any>(null);
    const subscriptionLink = useRef<string>('');
    const hasLoadMoreElements = useRef<boolean>(false);
    const savedIntegrationData = useRef<any>(null);
    const currentOpenedFolders = useRef<any>(null);

    useEffect(() => {
        // Event listeners

        const listeners: Record<string, (e: any) => void> = {
            refreshFilesList: (e: any) => {
                if (e.detail.refreshIntegration) {
                    getIntegrationData({ hardReload: true });
                } else if (e.detail.refreshCrowdin) {
                    getCrowdinData();
                }
            },
            crowdinFilesFolderToggled: (event: any) => {
                if (event.detail.componentId === 'crowdin-files' && event.detail.isOpen && event.detail.type === '1') {
                    getFileProgress(event.detail.id);
                }

                if (integrationOneLevelFetching) {
                    if (event.detail.componentId === 'integration-files' && event.detail.isOpen) {
                        getIntegrationData({ parentId: event.detail.id, recursion: event.detail.recursion });
                    }
                }
            },
            uploadFilesToCrowdin: uploadFilesToCrowdin,
            uploadFilesToIntegration: uploadFilesToIntegration,
            cancelAsyncAction: (e: any) => {
                cancelJob(e.detail);
            },
            keydown: (event: any) => {
                if (event.keyCode === 27) {
                    const permissions = document.getElementById('permissions-modal');

                    // not sure where we get users from here
                    // @ts-ignore
                    if (users) {
                        closeModal(permissions);
                    }

                    const infoModalEl = document.getElementById('info-modal');
                    if (infoModalEl) {
                        closeModal(infoModalEl);
                    }

                    const settingsModal = document.getElementById('settings-modal');
                    if (settingsModal) {
                        closeModal(settingsModal);
                    }

                    if (languageModal.current) {
                        closeModal(languageModal.current);
                    }
                }
            },
            loadMoreClick: async (e: any) => {
                const loadMoreFile = e.detail;
                const fileComponent = document.getElementById('integration-files') as any;

                await fileComponent.setLoadMoreLoading(loadMoreFile.id, true);

                getIntegrationData({ parentId: e.detail.parent_id, paginationData: e.detail.pagination }).finally(
                    async () => {
                        await fileComponent.setLoadMoreLoading(loadMoreFile.id, false);
                    },
                );
            },
        };

        if (integrationSearchListener) {
            listeners['integrationFilterChange'] = async (event: any) => {
                const isEmptySearch = !event.detail || event.detail.trim() === '';

                if (isEmptySearch && savedIntegrationData.current && hasLoadMoreElements.current) {
                    appComponent.current.setIntegrationFilesData(savedIntegrationData.current);
                    appComponent.current.setIntegrationOpenedFolders(currentOpenedFolders.current);
                    return;
                }

                if (!isEmptySearch && hasLoadMoreElements.current) {
                    savedIntegrationData.current = await appComponent.current.getIntegrationFilesData();
                    currentOpenedFolders.current = await appComponent.current.getIntegrationOpenedFolders();
                }

                getIntegrationData({ search: event.detail });
            };
        }

        if (integrationPagination) {
            listeners['fileListEnd'] = (e: any) => {
                getIntegrationData({ page: e.detail.page });
            };
        }

        if (withCronSync || webhooks) {
            listeners['integrationScheduleSync'] = setIntegrationScheduleSync;
            listeners['crowdinScheduleSync'] = setCrowdinScheduleSync;
            listeners['crowdinDisableSync'] = disableCrowdinSync;
            listeners['integrationDisableSync'] = disableIntegrationSync;
        }

        for (const [event, handler] of Object.entries(listeners)) {
            document.body.addEventListener(event, handler);
        }

        // Initial data fetch

        getCrowdinData();
        getIntegrationData({});
        getActiveJobs();

        if (checkSubscription) {
            getSubscriptionInfo();
        }

        if (notice) {
            const noticeEl = document.getElementById('notice');
            checkAlert(noticeEl, 'notice');
        }

        return () => {
            for (const [event, handler] of Object.entries(listeners)) {
                document.body.removeEventListener(event, handler);
            }
        };
    }, []);

    // Expose centralized API to global scope for main.js compatibility
    useEffect(() => {
        (window as any).app = {
            subscriptionModal: subscriptionModal,
            subscriptionLink: subscriptionLink,
            openModal: openModal,
        };

        return () => {
            delete (window as any).app;
        };
    }, []);

    // crowdin-users-select doesn't trigger onChange reliably, using native event listener instead
    useEffect(() => {
        const selectElement = usersSelectRef.current;
        if (selectElement) {
            const handleChange = () => {
                checkUsers();
            };

            selectElement.addEventListener('change', handleChange);

            return () => {
                selectElement.removeEventListener('change', handleChange);
            };
        }
    }, []);

    function integrationLogout() {
        checkOrigin()
            .then((queryParams) => fetch(`api/logout${queryParams}`, { method: 'POST' }))
            .then(checkResponse)
            .then(reloadLocation)
            .then(() => localStorage.removeItem(`revised_${name}`))
            .catch((e) => catchRejection(e, e.error || 'Looks like you are not logged in'));
    }

    function transformCrowdinItemToTree(e: any) {
        const item: any = {
            parent_id: e.parentId ? e.parentId : '0',
            name: e.name,
            id: e.id,
            customContent: e.customContent,
            labels: e.labels,
            disabled: e.disabled,
            tooltip: e.tooltip,
        };
        if (e.type) {
            item.type = e.type;
            item.node_type = fileType;
            item.failed = e.failed;
            item.excludedTargetLanguages = e.excludedTargetLanguages;
        } else {
            item.node_type = e.nodeType || folderType;
        }
        return item;
    }

    function getCrowdinData() {
        appComponent.current?.setAttribute('is-crowdin-loading', 'true');

        let promise;
        if (progressiveCrowdinFilesLoading) {
            promise = checkOrigin()
                .then((restParams) => {
                    const dirPromise = fetch('api/crowdin/files' + restParams + '&mode=directories')
                        .then(checkResponse)
                        .then((directories) => {
                            const dirTree = directories.map(transformCrowdinItemToTree);
                            crowdinData.current = dirTree;
                            appComponent.current?.setCrowdinFilesData(dirTree);
                            return dirTree;
                        });

                    const filesPromise = fetch('api/crowdin/files' + restParams + '&mode=files').then(checkResponse);

                    const projectPromise = fetch('api/crowdin/project' + restParams).then(checkResponse);

                    return Promise.all([dirPromise, filesPromise, projectPromise]);
                })
                .then(([dirTree, files, projectData]) => {
                    const fileTree = files.map(transformCrowdinItemToTree);
                    crowdinData.current = [...dirTree, ...fileTree];
                    appComponent.current?.setCrowdinFilesData(crowdinData.current);

                    project.current = projectData;
                    const languagesSorted = project.current.targetLanguages
                        .map((language: any) => ({
                            sourceLanguageId: projectData.sourceLanguage.editorCode,
                            projectEditorLink: projectData.projectEditorLink,
                            ...language,
                        }))
                        .sort((a: any, b: any) => a.name.localeCompare(b.name));

                    if (project.current.inContext && config.current?.inContext) {
                        languagesSorted.push({ ...project.current.inContextPseudoLanguage, inContext: true });
                    }

                    setLanguagesForTranslation(languagesSorted);
                    appComponent.current?.setCrowdinLanguagesData(languagesSorted);
                });
        } else {
            promise = checkOrigin()
                .then((restParams) => {
                    const filesPromise = fetch('api/crowdin/files' + restParams).then(checkResponse);

                    const projectPromise = fetch('api/crowdin/project' + restParams).then(checkResponse);

                    return Promise.all([filesPromise, projectPromise]);
                })
                .then(([files, projectData]) => {
                    const tree = files.map(transformCrowdinItemToTree);
                    crowdinData.current = tree;
                    appComponent.current.setCrowdinFilesData(tree);

                    project.current = projectData;
                    const languagesSorted = project.current.targetLanguages
                        .map((language: any) => ({
                            sourceLanguageId: projectData.sourceLanguage.editorCode,
                            projectEditorLink: projectData.projectEditorLink,
                            ...language,
                        }))
                        .sort((a: any, b: any) => a.name.localeCompare(b.name));

                    if (project.current.inContext && config.current?.inContext) {
                        languagesSorted.push({ ...project.current.inContextPseudoLanguage, inContext: true });
                    }

                    setLanguagesForTranslation(languagesSorted);
                    appComponent.current.setCrowdinLanguagesData(languagesSorted);
                });
        }

        promise
            .then(() => {
                if (withCronSync || webhooks) {
                    return getSyncSettings('crowdin');
                }
            })
            .catch((e) => catchRejection(e, "Can't fetch Crowdin data"))
            .finally(() => appComponent.current.setAttribute('is-crowdin-loading', false));
    }

    function getIntegrationData(
        { hardReload = false, parentId = '', search = '', page = 0, recursion = false, paginationData } = {} as any,
        recursionState: any = null,
    ) {
        if (!paginationData) {
            appComponent.current.setAttribute('is-integration-loading', true);
        }

        let recursionStateLocal: any = recursionState;

        return checkOrigin()
            .then((restParams) => {
                let url = `api/integration/data${restParams}&parent_id=${encodeURIComponent(parentId)}&search=${encodeURIComponent(search)}&page=${page}`;
                if (paginationData) {
                    url += `&paginationData=${encodeURIComponent(JSON.stringify(paginationData))}`;
                }
                return fetch(url);
            })
            .then(checkResponse)
            .then(async (res) => {
                const files = res.data;
                const stopPagination = res.stopPagination;
                const tree = files.map((e: any) => {
                    const item = {
                        parent_id: e.parentId ? e.parentId : '0',
                        name: e.name,
                        id: e.id,
                        customContent: e.customContent,
                        labels: e.labels,
                        disabled: e.disabled,
                        tooltip: e.tooltip,
                        pagination: e.pagination,
                    } as any;
                    if (e.type) {
                        item.isNew = e.isNew;
                        item.isUpdated = e.isUpdated;
                        item.notSynced = e.notSynced;
                        item.synced = e.synced;
                        item.type = e.type;
                        item.node_type = fileType;
                    } else {
                        item.node_type = e.nodeType || folderType;
                    }
                    return item;
                });

                if ((!paginationData && !parentId && !search) || hardReload) {
                    hasLoadMoreElements.current = tree.some((item: any) => item.node_type === 'load-more');
                }

                const appIntegrationFiles = appComponent.current.querySelector('#integration-files');
                if (hardReload) {
                    appComponent.current.setIntegrationFilesData(tree);
                } else if (tree.length) {
                    appComponent.current.pushIntegrationFilesData(tree).then(() => {
                        if (parentId) {
                            appIntegrationFiles.getSelected().then(async (selection: any) => {
                                const selectedIds = selection
                                    ?.filter((node: any) => node)
                                    .map(({ id }: any) => id.toString());
                                const filteredNodes = tree.filter(
                                    (node: any) =>
                                        !selectedIds.includes(node.id.toString()) &&
                                        selectedIds.includes(node.parent_id.toString()),
                                );

                                if (filteredNodes?.length) {
                                    const filteredNodesId = filteredNodes.map(({ id }: any) => id.toString());
                                    await appIntegrationFiles.setSelected(filteredNodesId);
                                }
                            });
                        }
                    });
                }

                if (integrationPagination) {
                    if (stopPagination) {
                        appIntegrationFiles.setAttribute('load-more-disabled', true);
                    } else {
                        appIntegrationFiles.setAttribute('load-more-disabled', false);
                    }
                }

                if (search) {
                    const openIds = files.filter((e: any) => !e.type).map((e: any) => e.id);
                    appComponent.current.setIntegrationOpenedFolders(openIds);
                }

                if (recursion) {
                    if (!recursionStateLocal) {
                        recursionStateLocal = {
                            openedFolders: new Set(),
                            calls: 0,
                            setLoading(isLoading: boolean) {
                                appComponent.current.setAttribute('is-integration-loading', isLoading);
                            },
                        };
                        recursionStateLocal.setLoading(true);
                    }

                    if (parentId) {
                        recursionStateLocal.openedFolders.add(parentId);
                    }

                    const folderItems = files.filter(isFolder);

                    if (folderItems.length === 0) {
                        return Promise.resolve();
                    }

                    folderItems.forEach((folder: any) => recursionStateLocal.openedFolders.add(folder.id));

                    const currentOpenedFolders = await appComponent.current.getIntegrationOpenedFolders();
                    appComponent.current.setIntegrationOpenedFolders([
                        ...currentOpenedFolders,
                        ...recursionStateLocal.openedFolders,
                    ]);

                    const recursivePromises = folderItems.map(async (folder: any) => {
                        recursionStateLocal.calls++;
                        try {
                            return getIntegrationData({ parentId: folder.id, recursion: true }, recursionState);
                        } finally {
                            recursionStateLocal.calls--;
                            if (recursionStateLocal.calls === 0) {
                                recursionStateLocal.setLoading(false);
                            }
                        }
                    });

                    return Promise.all(recursivePromises);
                }
            })
            .then(() => {
                if (withCronSync || webhooks) {
                    getSyncSettings('integration');
                }
            })
            .catch((e) => catchRejection(e, `Can't fetch ${name} templates`))
            .finally(() => {
                if (!recursionStateLocal && !recursion) {
                    appComponent.current.setAttribute('is-integration-loading', false);
                } else if (recursionStateLocal && recursionStateLocal.calls === 0) {
                    appComponent.current.setAttribute('is-integration-loading', false);
                }
            });
    }

    function isScheduleDisabled(currentConfig = config.current): boolean {
        return String(currentConfig?.schedule ?? '0') === '0';
    }

    async function applyAutoSyncPausedMarkers(provider: string, syncSettingsFiles: any[] = []) {
        const isDisabled = isScheduleDisabled();
        const configuredIds = new Set(
            (Array.isArray(syncSettingsFiles) ? syncSettingsFiles : []).map((item: any) => `${item.id}`),
        );

        const getChangedItems = (tree: any[] = []) =>
            tree
                .map((item: any) => {
                    const nextScheduleDisabled =
                        Boolean(item.schedule) && isDisabled && configuredIds.has(`${item.id}`);
                    const currentScheduleDisabled = Boolean(item.scheduleDisabled);

                    if (currentScheduleDisabled === nextScheduleDisabled) {
                        return null;
                    }

                    return {
                        ...item,
                        scheduleDisabled: nextScheduleDisabled,
                    };
                })
                .filter(Boolean);

        if (provider === 'crowdin') {
            const changedItems = getChangedItems(crowdinData.current || []);
            if (changedItems.length) {
                const changedById = new Map(changedItems.map((item: any) => [`${item.id}`, item]));
                crowdinData.current = (crowdinData.current || []).map(
                    (item: any) => changedById.get(`${item.id}`) || item,
                );
                appComponent.current?.updateCrowdinFileData(changedItems);
            }
            return;
        }

        const integrationData = await appComponent.current?.getIntegrationFilesData?.();
        if (Array.isArray(integrationData)) {
            const changedItems = getChangedItems(integrationData);
            if (changedItems.length) {
                appComponent.current?.updateIntegrationFileData(changedItems);
            }
        }
    }

    async function fetchSyncSettingsFiles(provider: string): Promise<any[]> {
        const restParams = await checkOrigin();
        const res = await fetch(`/api/sync-settings/${provider}` + restParams).then(checkResponse);
        return Array.isArray(res) ? res : [];
    }

    async function refreshAutoSyncPausedIndicatorsAndWarning() {
        const [crowdinSyncSettings, integrationSyncSettings] = await Promise.all([
            fetchSyncSettingsFiles('crowdin'),
            fetchSyncSettingsFiles('integration'),
        ]);

        await applyAutoSyncPausedMarkers('crowdin', crowdinSyncSettings);
        await applyAutoSyncPausedMarkers('integration', integrationSyncSettings);

        if (isScheduleDisabled() && (crowdinSyncSettings.length || integrationSyncSettings.length)) {
            showToast('Auto Sync is configured for some files, but sync schedule is disabled in app settings.');
        }
    }

    function getSyncSettings(provider: string) {
        checkOrigin()
            .then((restParams) => fetch(`/api/sync-settings/${provider}` + restParams))
            .then(checkResponse)
            .then(async (res) => {
                if (provider === 'crowdin') {
                    if (syncNewElements?.crowdin) {
                        appComponent.current.setCrowdinScheduleSync(res, true, true);
                    } else {
                        appComponent.current.setCrowdinScheduleSync(res);
                    }
                } else {
                    if (syncNewElements?.integration) {
                        appComponent.current.setIntegrationScheduleSync(res, true, true);
                    } else {
                        appComponent.current.setIntegrationScheduleSync(res);
                    }
                }
                await applyAutoSyncPausedMarkers(provider, res);
            })
            .catch((e) => catchRejection(e, "Can't fetch file progress"));
    }

    function getFileProgress(fileId: string) {
        checkOrigin()
            .then((restParams) => fetch(`api/crowdin/file-progress/${fileId}` + restParams))
            .then(checkResponse)
            .then((res) => appComponent.current.addCrowdinFileProgress(res))
            .catch((e) => catchRejection(e, "Can't fetch file progress"));
    }

    function uploadFilesToCrowdin(event: any, languages = []) {
        let files = [];
        let uploadTranslations = false;
        let forcePushSources = false;
        if (event.detail?.action === 'uploadTranslations') {
            files = event.detail.files;
            uploadTranslations = true;
            forcePushSources = true;
        } else if (event.detail?.action === 'excludedTargetLanguages') {
            fileToSync.current = event.detail.files;
            openLanguageModal();
            return;
        } else if (event.detail?.action === 'forcePushSources') {
            files = event.detail.files;
            forcePushSources = true;
        } else if (event.detail?.action === 'syncTranslationsToIntegration') {
            syncTranslationsToIntegration(event.detail.files);
            return;
        } else {
            files = event.detail;
        }

        if (languages.length > 0) {
            forcePushSources = true;
        }

        if (event.detail?.length === 0 || !event.detail) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }
        appComponent.current.setAttribute('is-to-crowdin-process', true);
        let req: any;

        if (integrationOneLevelFetching) {
            req = files.map((f: any) => ({
                name: f.name,
                id: f.id,
                type: f.type,
                parentId: f.parent_id,
                nodeType: f.node_type,
                isUpdated: f.isUpdated,
            }));
        } else {
            req = files
                .filter((f: any) => f.node_type !== folderType)
                .map((f: any) => ({
                    name: f.name,
                    id: f.id,
                    type: f.type,
                    parentId: f.parent_id,
                    isUpdated: f.isUpdated,
                }));
        }

        checkOrigin()
            .then((restParams) =>
                fetch(
                    `api/crowdin/update${restParams}&uploadTranslations=${uploadTranslations}&languages=${languages}&forcePushSources=${forcePushSources}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(req),
                    },
                ),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateCrowdin,
                });
            })
            .catch((e) => catchRejection(e, "Can't upload templates to Crowdin"));
    }

    function getActiveJobs() {
        checkOrigin()
            .then((restParams) => fetch(`api/jobs${restParams}`))
            .then(checkResponse)
            .then((jobs) => {
                if (!Array.isArray(jobs)) {
                    return;
                }

                jobs.forEach((job) => {
                    switch (job.type) {
                        case JOB_TYPE.updateCrowdin:
                        case JOB_TYPE.updateTargetLanguages:
                            appComponent.current.setAttribute('is-to-crowdin-process', true);
                            break;
                        case JOB_TYPE.updateIntegration:
                            appComponent.current.setAttribute('is-to-integration-process', true);
                            break;
                        case JOB_TYPE.crowdinSyncSettingsSave:
                            appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
                            break;
                        case JOB_TYPE.integrationSyncSettingsSave:
                            appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);
                            break;
                        default:
                    }

                    checkJob({
                        jobId: job.id,
                        jobType: job.type,
                    });
                });
            })
            .catch((e) => catchRejection(e, 'Sync status check failed'));
    }

    function cancelJob(jobId: string) {
        if (asyncJobs.current[jobId]?.isFailed) {
            return;
        }

        checkOrigin()
            .then((restParams) =>
                fetch('api/jobs' + restParams + '&jobId=' + jobId, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                }),
            )
            .then(checkResponse)
            .then(() => showToast('Cancellation…'))
            .catch((e) => catchRejection(e, 'Sync cancellation failed'));
    }

    function checkJob({ jobId, jobType, onSuccess, onError, onFinally }: any) {
        switch (jobType) {
            case JOB_TYPE.updateCrowdin:
            case JOB_TYPE.updateTargetLanguages:
                if (!onFinally) {
                    onFinally = () => {
                        appComponent.current.setAttribute('is-to-crowdin-process', false);
                        getCrowdinData();
                    };
                }
                break;
            case JOB_TYPE.updateIntegration:
                if (!onFinally) {
                    onFinally = () => appComponent.current.setAttribute('is-to-integration-process', false);
                }
                break;
            case JOB_TYPE.crowdinSyncSettingsSave:
                if (!onFinally) {
                    onFinally = () => appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', false);
                }
                break;
            case JOB_TYPE.integrationSyncSettingsSave:
                if (!onFinally) {
                    onFinally = () =>
                        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', false);
                }
                break;
            default:
        }

        checkOrigin()
            .then((restParams) => fetch('api/jobs' + restParams + '&jobId=' + jobId))
            .then(checkResponse)
            .then((job) => {
                const isFailed = [JOB_STATUS.failed, JOB_STATUS.canceled].includes(job.status);
                const progress = isFailed || JOB_STATUS.finished === job.status ? 100 : job.progress;
                const info = JOB_STATUS.canceled === job.status ? `Cancelled\n${job.info || ''}` : job.info;

                if (!silentJobs.find((type) => type === jobType)) {
                    pushJobs([
                        {
                            id: job.id,
                            title: job.title,
                            progress,
                            info,
                            isFailed,
                        },
                    ]);
                }

                if (isFailed) {
                    if (onError) {
                        onError();
                    }
                } else if ([JOB_STATUS.created, JOB_STATUS.inProgress].includes(job.status) && job.progress < 100) {
                    setTimeout(() => {
                        checkJob({ jobId, jobType, onSuccess, onError, onFinally });
                    }, asyncProgress.checkInterval);
                    return;
                } else {
                    if (onSuccess) {
                        onSuccess(job);
                    }
                }

                if (onFinally) {
                    onFinally(job);
                }
            })
            .catch((e) => {
                if (onFinally) {
                    onFinally();
                }

                if (!silentJobs.find((type) => type === jobType)) {
                    pushJobs([
                        {
                            id: jobId,
                            isFailed: true,
                        },
                    ]);
                }

                showToast('Sync status check failed');
            });
    }

    function pushJobs(jobs: any[]) {
        const el = document.querySelector('crowdin-async-progress') as any;
        if (el && el.pushJobs) {
            el.pushJobs(jobs);
            jobs.forEach((job) => (asyncJobs.current[job.id] = job));
        } else {
            setTimeout(() => {
                pushJobs(jobs);
            }, 300);
        }
    }

    function uploadFilesToIntegration(event: any) {
        let files = {} as any;
        let forcePushTranslations = false;
        if (event.detail?.action === 'forcePushTranslations') {
            files = event.detail.files;
            forcePushTranslations = true;
        } else if (event.detail?.action === 'excludedTargetLanguages') {
            crowdinFilesToUpdateTargetLanguages.current = event.detail.files;
            openLanguageModalForCrowdinFiles();
            return;
        } else if (event.detail?.action === 'syncSourcesToCrowdin') {
            syncToCrowdinFromCrowdinFiles(event.detail.files, false);
            return;
        } else if (event.detail?.action === 'syncTranslationsToCrowdin') {
            syncToCrowdinFromCrowdinFiles(event.detail.files, true);
            return;
        } else {
            files = event.detail;
        }

        if (Object.keys(event.detail).length === 0 || !event.detail) {
            showToast('Select files which will be uploaded to ${name}');
            return;
        }

        appComponent.current.setAttribute('is-to-integration-process', true);
        const req = {} as any;
        Object.keys(files)
            .filter((id) => crowdinData.current.find((c) => c.id === id).node_type === fileType)
            .forEach((id) => (req[id] = files[id]));
        checkOrigin()
            .then((restParams) =>
                fetch(`api/integration/update${restParams}&forcePushTranslations=${forcePushTranslations}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(req),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateIntegration,
                });
            })
            .catch((e) => catchRejection(e, `Can't upload files to ${name}`));
    }

    function syncToCrowdinFromCrowdinFiles(files: any, uploadTranslations: any) {
        if (!files || Object.keys(files).length === 0) {
            showToast('Select files to sync');
            return;
        }

        appComponent.current.setAttribute('is-to-crowdin-process', true);
        const req = {} as any;
        Object.keys(files)
            .filter((id) => crowdinData.current.find((c) => c.id === id)?.node_type === fileType)
            .forEach((id) => (req[id] = files[id]));

        checkOrigin()
            .then((restParams) =>
                fetch(
                    `api/crowdin/update${restParams}&matchFromCrowdin=true&uploadTranslations=${uploadTranslations}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(req),
                    },
                ),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateCrowdin,
                });
            })
            .catch((e) => catchRejection(e, "Can't sync files to Crowdin"));
    }

    function syncTranslationsToIntegration(files: any[]) {
        if (!files || files.length === 0) {
            showToast('Select files to sync');
            return;
        }
        appComponent.current.setAttribute('is-to-integration-process', true);
        const req = files
            .filter((f) => f.node_type !== folderType)
            .map((f) => ({
                name: f.name,
                id: f.id,
                type: f.type,
                parentId: f.parent_id,
            }));
        checkOrigin()
            .then((restParams) =>
                fetch(`api/integration/update${restParams}&matchFromIntegration=true`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(req),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateIntegration,
                });
            })
            .catch((e) => catchRejection(e, `Can't sync translations to ${name}`));
    }

    function openLanguageModal() {
        openModal(languageModal.current);
    }

    function openLanguageModalForCrowdinFiles() {
        const languages = project.current.targetLanguages || [];

        const buttonHtml = `
        <crowdin-button id="update-crowdin-target-languages" outlined">
            Update Target Languages
        </crowdin-button>
      `;

        setTimeout(() => {
            const btn = document.querySelector('#update-crowdin-target-languages');
            if (btn) {
                btn.addEventListener('click', () => updateCrowdinFilesTargetLanguages());
            }
        }, 0);

        setLanguagesForTranslation(languages, buttonHtml);
        openModal(languageModal.current);
    }

    async function uploadFilesToCrowdinWithExcludedLanguages() {
        const languageSelect = document.querySelector('#language-select') as any;
        const selectedLanguages = await languageSelect.getValue();
        closeModal(languageModal.current);

        uploadFilesToCrowdin({ detail: fileToSync.current }, selectedLanguages);
    }

    async function updateCrowdinFilesTargetLanguages() {
        const languageSelect = document.querySelector('#language-select') as any;
        const selectedLanguages = await languageSelect.getValue();
        closeModal(languageModal.current);

        const fileIds = Object.keys(crowdinFilesToUpdateTargetLanguages.current).map((id) => parseInt(id, 10));

        if (fileIds.length === 0) {
            showToast('Select files to update');
            return;
        }

        appComponent.current.setAttribute('is-to-crowdin-process', true);

        checkOrigin()
            .then((restParams) =>
                fetch(`api/crowdin/files-target-languages${restParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ fileIds, languages: selectedLanguages }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: JOB_TYPE.updateTargetLanguages,
                });
            })
            .catch((e) => catchRejection(e, "Can't update files target languages"));
    }

    async function setLanguagesForTranslation(languages: any[], buttonHtml?: string) {
        const defaultButtonHtml = `
        <crowdin-button id="upload-exclude-languages" outlined">
            <div class="integration-icon-button">
              <span>Sync to</span>
              <crowdin-logo is-icon/>
            </div>
        </crowdin-button>
      `;

        setTimeout(() => {
            const btn = document.querySelector('#upload-exclude-languages');
            if (btn) {
                btn.addEventListener('click', () => uploadFilesToCrowdinWithExcludedLanguages());
            }
        }, 0);

        const languageIds = languages.map((language) => language.id);

        if (languageModal.current) {
            let modalContent = `<div>
        <crowdin-select
          is-multi
          is-searchable
          is-position-fixed
          close-on-select="false"
          name="languages"
          id="language-select"
          label="Languages"
        >
        `;

            for (const language of languages) {
                modalContent += `<option value="${language.id}">${language.name}</option>`;
            }

            modalContent += `
        </crowdin-select>
        </div>
        <div slot="footer">
            ${buttonHtml || defaultButtonHtml}
        </div>
        `;

            languageModal.current.innerHTML = modalContent;

            const select = document.querySelector('#language-select') as any;
            await select.setValue(languageIds);
        }
    }

    function hideConfirmUsersBlock() {
        const confirmUsersBlock = document.querySelector('.confirm-users-block') as any;
        confirmUsersBlock.classList.add('hidden');

        const permissionsModal = document.getElementById('permissions-modal') as any;
        permissionsModal.setAttribute('body-overflow-unset', true);
    }

    function getUserFullName(user: any) {
        if (user.full_name) {
            return user.full_name;
        }

        const ownerFullName = user.name || ((user.first_name || '') + ' ' + (user.last_name || '')).trim();

        return !!ownerFullName && user?.login !== ownerFullName ? `${ownerFullName} (${user?.login})` : user?.login;
    }

    function showPermissionsDialog() {
        const permissions = document.getElementById('permissions-modal') as any;

        hideConfirmUsersBlock();
        openModal(permissions);
        setLoader('#permissions-modal');
        const select = document.getElementById('users') as any;

        select.value = '[]';

        Promise.all([
            new Promise((resolve) => {
                checkOrigin()
                    .then((restParams) => fetch('api/users' + restParams))
                    .then(checkResponse)
                    .then((res) => {
                        resolve(JSON.stringify(res.data.managers));
                    });
            }),
            new Promise((resolve) => {
                AP.getUsers((res: any) => {
                    const users = Object.values(res).filter((user: any) => user.id !== Number(userId));
                    resolve(users);
                });
            }),
        ])
            .then(([managers, users]) => {
                const parsedManagers = (JSON.parse(managers as string) || []) as string[];
                const availableUsers = users as any[];
                const validUserIds = new Set(availableUsers.map((user: any) => `${user.id}`));
                const filteredManagers = parsedManagers.filter((id: string) => validUserIds.has(id));

                select.innerHTML = availableUsers
                    .map((user: any) => `<option value="${user.id}">${sanitizeHTML(getUserFullName(user))}</option>`)
                    .join('');
                select.value = JSON.stringify(filteredManagers);
            })
            .catch((e) => catchRejection(e, "Can't fetch users"))
            .finally(() => unsetLoader('#permissions-modal'));
    }

    function showConfirmUsersBlock() {
        const confirmUsersBlock = document.querySelector('.confirm-users-block') as any;
        confirmUsersBlock.classList.remove('hidden');

        const permissionsModal = document.getElementById('permissions-modal') as any;
        permissionsModal.removeAttribute('body-overflow-unset');
    }

    function checkUsers() {
        setLoader('#permissions-modal');

        const select = document.getElementById('users') as any;

        if (select.value === '[]') {
            hideConfirmUsersBlock();
            unsetLoader('#permissions-modal');
            return;
        }

        const users = JSON.parse(select.value) as any[];
        const user_ids = users.filter((id) => parseInt(id));
        const emails = users.filter((id) => !parseInt(id));

        const params = { user_ids, emails };

        Promise.all([
            new Promise((resolve) => {
                checkOrigin()
                    .then((restParams) => fetch('api/users' + restParams))
                    .then(checkResponse)
                    .then((usersResponse) => {
                        resolve(usersResponse.data.managers);
                    });
            }) as Promise<any>,
            new Promise((resolve) => {
                AP.validateUsers(params, (res) => {
                    resolve(res.data);
                });
            }) as Promise<any>,
            new Promise((resolve) => {
                AP.getUsers((res) => {
                    resolve(res);
                });
            }) as Promise<any>,
        ])
            .then(([appManagers, checkUsersResponse, allUsers]) => {
                const newUserEmails = checkUsersResponse.newUserEmails.map((email: string) => ({ name: email }));

                const newProjectManagers = [
                    ...newUserEmails,
                    ...checkUsersResponse.newProjectManagers.map((user: any) => ({
                        name: getUserFullName(user),
                    })),
                ];

                const newApplicationAccessUsers = [
                    ...newUserEmails,
                    ...checkUsersResponse.newApplicationAccessUsers.map((user: any) => ({
                        name: getUserFullName(user),
                    })),
                ];

                const newApplicationManagers = [
                    ...newUserEmails,
                    ...Object.values(allUsers as any)
                        .filter(
                            (user: any) =>
                                users.some((id) => +id === user.id) && !appManagers.some((id: any) => +id === user.id),
                        )
                        .map((user) => ({ name: getUserFullName(user) })),
                ];

                prepareUsersConfirmBlock({
                    ...checkUsersResponse,
                    newUserEmails,
                    newProjectManagers,
                    newApplicationAccessUsers,
                    newApplicationManagers,
                });
            })
            .catch((e) => catchRejection(e, e?.error || "Can't invite users"))
            .finally(() => unsetLoader('#permissions-modal'));
    }

    function inviteUsers() {
        setLoader('#permissions-modal');

        const select = document.getElementById('users') as any;
        const users = JSON.parse(select.value) as any[];
        const user_ids = users.filter((id) => parseInt(id));
        const emails = users.filter((id) => !parseInt(id));

        const params = { user_ids, emails };

        AP.inviteUsers(params, (res) => {
            if (!res.success) {
                catchRejection(res, res?.error?.message || "Can't invite users");
                unsetLoader('#permissions-modal');
                return;
            }

            checkOrigin()
                .then((restParams) =>
                    fetch('api/invite-users' + restParams, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(res.data),
                    }),
                )
                .then(checkResponse)
                .then((res) => {
                    const permissions = document.getElementById('permissions-modal') as any;
                    if (!res.success) {
                        catchRejection(res, res?.message || "Can't invite users");
                        return;
                    }

                    showToast('Users successfully updated');
                    hideConfirmUsersBlock();
                    closeModal(permissions);
                })
                .catch((e) => catchRejection(e, e?.error || "Can't invite users"))
                .finally(() => unsetLoader('#permissions-modal'));
        });
    }

    function prepareUsersConfirmBlock(usersData: any) {
        showConfirmUsersBlock();

        const organizationInvite = document.querySelector('.organization-invite');
        const projectInvite = document.querySelector('.project-invite');
        const applicationCredentialsInvite = document.querySelector('.application-credentials-invite');

        const grantedElement = '<crowdin-p>&horbar;</crowdin-p>';
        const willGrantedElement = '<span class="badge badge-will-be-granted">Will Be Granted</span>';
        const notAvailableElement = '<span class="badge badge-not-available">Action Required</span>';

        // only in enterprise
        if (organizationInvite) {
            const organizationWillGrantElement = '<span class="badge badge-will-be-granted">Will Be Registered</span>';

            processUsersWhoWillBeInvitedToOrganization(
                organizationInvite,
                usersData,
                organizationWillGrantElement,
                grantedElement,
                notAvailableElement,
            );
        }

        processUsersWhoWillBeInvited(projectInvite, usersData.newProjectManagers, willGrantedElement, grantedElement);
        processUsersWhoWillBeInvited(
            applicationCredentialsInvite,
            usersData.newApplicationManagers,
            willGrantedElement,
            grantedElement,
        );

        processUsersWhoWillBeInvitedApplicationSettings(
            usersData,
            willGrantedElement,
            grantedElement,
            notAvailableElement,
        );
    }

    function processUsersWhoWillBeInvitedToOrganization(
        element: any,
        usersData: any,
        willGrantedElement: string,
        alreadyGrantedMessage: string,
        notAvailableElement: string,
    ) {
        const tooltip = element.querySelector('.status');
        const description = element.querySelector('.permission-description');
        const userList = element.querySelector('.affected-users');

        description.classList.remove('text-warning');
        description.innerText = '';

        const users = usersData.newUserEmails;

        if (users.length) {
            if (usersData.inviteRestricted) {
                description.classList.add('text-warning');
                description.innerText = 'New user invitations are restricted to administrators.';

                tooltip.innerHTML = notAvailableElement;
            } else {
                tooltip.innerHTML = willGrantedElement;
            }

            let affectedUsers = '<ul>';
            for (const user of users) {
                affectedUsers += `<li><crowdin-p>${sanitizeHTML(user.name)}</crowdin-p></li>`;
            }
            affectedUsers += '</ul>';
            userList.innerHTML = affectedUsers;
        } else {
            tooltip.innerHTML = alreadyGrantedMessage;
            userList.innerHTML = alreadyGrantedMessage;
        }
    }

    function processUsersWhoWillBeInvited(
        element: any,
        users: any,
        grantMessage: string,
        alreadyGrantedMessage: string,
    ) {
        const tooltip = element.querySelector('.status');
        const userList = element.querySelector('.affected-users');

        if (users.length) {
            tooltip.innerHTML = grantMessage;

            let affectedUsers = '<ul>';
            for (const user of users) {
                affectedUsers += `<li><crowdin-p>${sanitizeHTML(user.name)}</crowdin-p></li>`;
            }
            affectedUsers += '</ul>';
            userList.innerHTML = affectedUsers;
        } else {
            tooltip.innerHTML = alreadyGrantedMessage;
            userList.innerHTML = alreadyGrantedMessage;
        }
    }

    function processUsersWhoWillBeInvitedApplicationSettings(
        usersData: any,
        willGrantedElement: any,
        grantedElement: any,
        notAvailableElement: any,
    ) {
        const applicationSettingsInvite = document.querySelector('.application-settings-invite') as any;

        const tooltip = applicationSettingsInvite.querySelector('.status');
        const description = applicationSettingsInvite.querySelector('.permission-description');
        const userList = applicationSettingsInvite.querySelector('.affected-users');

        let descriptionMessage = 'This can be configured in organization settings (Apps section).';
        description.classList.remove('text-warning');
        description.innerText = descriptionMessage;

        let affectedUsers = '<ul>';

        if (!usersData.newApplicationAccessUsers.length) {
            tooltip.innerHTML = grantedElement;
            userList.innerHTML = grantedElement;
        } else {
            if (usersData.isAdmin) {
                tooltip.innerHTML = willGrantedElement;
                for (const user of usersData.newApplicationAccessUsers) {
                    affectedUsers += `<li><crowdin-p>${sanitizeHTML(user.name)}</crowdin-p></li>`;
                }
                userList.innerHTML = affectedUsers + '</ul>';
            } else {
                descriptionMessage += ' Only organization admins have permission to update this setting.';

                description.classList.add('text-warning');
                description.innerText = descriptionMessage;

                tooltip.innerHTML = notAvailableElement;
                for (const user of usersData.newApplicationAccessUsers) {
                    affectedUsers += `<li><crowdin-p>${sanitizeHTML(user.name)}</crowdin-p></li>`;
                }
                userList.innerHTML = affectedUsers + '</ul>';
            }
        }
    }

    function setLoader(id: string) {
        const loader = document.querySelector(`${id} .loader`) as any;
        loader.classList.remove('hidden');
    }

    function unsetLoader(id: string) {
        const loader = document.querySelector(`${id} .loader`) as any;
        setTimeout(function () {
            loader.classList.add('hidden');
        }, 500);
    }

    function triggerEvent(el: any, type: string) {
        const e = document.createEvent('HTMLEvents') as any;
        e.initEvent(type, false, true);
        el.dispatchEvent(e);
    }

    function fillSettingsForm() {
        Object.entries(config.current).forEach(([key, value]) => {
            const el = document.getElementById(`${key}-settings`) as any;
            if (el && (value || el.tagName.toLowerCase() === 'crowdin-checkbox')) {
                if (el.tagName.toLowerCase() === 'crowdin-select') {
                    if (el.hasAttribute('is-multi')) {
                        el.value = JSON.stringify(value);
                    } else {
                        el.value = JSON.stringify([value]);
                    }
                } else if (el.tagName.toLowerCase() === 'crowdin-checkbox') {
                    el.checked = !!value;
                } else if (el.tagName.toLowerCase() === 'crowdin-textarea') {
                    el.value = value;
                    el.setValue(value);
                } else {
                    el.value = value;
                }

                triggerEvent(el, 'change');
            }
        });

        // Reinitialize dependencies after form is filled
        setTimeout(() => {
            if (window.initializeDependencies) {
                window.initializeDependencies();
            }
        }, 100);
    }

    function saveSettings() {
        const settingsSaveBtn = document.getElementById('settings-save-btn') as any;

        setLoader('#settings-modal');
        const settingsElements = Array.from(document.querySelectorAll('#modal-content crowdin-tabs > div'));
        const tags = ['crowdin-checkbox', 'crowdin-select', 'crowdin-input', 'crowdin-textarea'];
        const configReq = {} as any;

        settingsElements.forEach((tab) => {
            const formElements = Array.from(tab.querySelectorAll('.crowdin-form > *'));
            formElements
                .filter((e) => tags.includes(e.tagName.toLowerCase()))
                .forEach((e: any) => {
                    const key = e.getAttribute('_key');
                    let value;
                    if (e.tagName.toLowerCase() === 'crowdin-select') {
                        value = JSON.parse(e.value);
                        if (!e.hasAttribute('is-multi')) {
                            value = value.length > 0 ? value[0] : undefined;
                        }
                    } else if (e.tagName.toLowerCase() === 'crowdin-checkbox') {
                        value = !!e.checked;
                    } else {
                        value = e.value;
                    }
                    configReq[key] = value;
                });
        });

        let isValidationError = false;
        let isSettingsSaveJobPending = false;
        let isSettingsSaveFailed = false;
        settingsSaveBtn.setAttribute('disabled', true);
        checkOrigin()
            .then((restParams) =>
                fetch('api/settings' + restParams, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ config: configReq }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                if (res?.jobId) {
                    isSettingsSaveJobPending = true;
                    checkJob({
                        jobId: res.jobId,
                        jobType: JOB_TYPE.integrationSettingsSave,
                        onSuccess: () => {
                            showToast('Settings successfully saved');
                            config.current = configReq;
                            refreshAutoSyncPausedIndicatorsAndWarning().catch((e) =>
                                catchRejection(e, "Can't fetch sync settings warning"),
                            );
                        },
                        onError: () => {
                            isSettingsSaveFailed = true;
                            showToast("Can't save settings");
                        },
                        onFinally: () => {
                            unsetLoader('#settings-modal');
                            settingsSaveBtn.removeAttribute('disabled');
                            if (!isValidationError && !isSettingsSaveFailed) {
                                const settingsModal = document.getElementById('settings-modal');
                                closeModal(settingsModal);
                                if (reloadOnConfigSave) {
                                    getIntegrationData({ hardReload: true });
                                    getCrowdinData();
                                }
                            }
                        },
                    });
                    return;
                }

                showToast('Settings successfully saved');
                config.current = configReq;
                refreshAutoSyncPausedIndicatorsAndWarning().catch((e) =>
                    catchRejection(e, "Can't fetch sync settings warning"),
                );
            })
            .catch((e) => {
                if (e?.error && e?.error?.type === 'validation_error') {
                    Object.keys(e?.error?.details).forEach((key) => {
                        const el = document.getElementById(`${key}-settings`);
                        if (el) {
                            el.setAttribute('error', 'true');
                            el.setAttribute('error-text', e?.error?.details[key]);
                        }
                    });

                    isValidationError = true;
                    showToast(e?.error?.details?.message || "Can't save settings");
                    settingsSaveBtn.removeAttribute('disabled');
                    return;
                }
                catchRejection(e, "Can't save settings");
            })
            .finally(() => {
                if (isSettingsSaveJobPending) {
                    return;
                }
                unsetLoader('#settings-modal');
                settingsSaveBtn.removeAttribute('disabled');
                if (!isValidationError) {
                    const settingsModal = document.getElementById('settings-modal');
                    closeModal(settingsModal);
                    if (reloadOnConfigSave) {
                        getIntegrationData({ hardReload: true });
                        getCrowdinData();
                    }
                }
            });
    }

    function getIntegrationFoldersToExpand(allData: any, syncedFiles: any) {
        return Array.isArray(allData)
            ? allData
                  .filter(
                      (node) =>
                          node.node_type === folderType &&
                          !syncedFiles.find((syncedFile: any) => syncedFile.parent_id === node.id),
                  )
                  .map((node) => ({
                      ...node,
                      nodeType: node.node_type,
                      parentId: node.parent_id,
                  }))
            : [];
    }

    async function saveScheduleSync() {
        const scheduleModal = document.getElementById('confirm-schedule-modal') as any;
        const newFile = scheduleModal.querySelector('#new-files').checked || false;
        const selectedFiles = scheduleModal.querySelector('#selected-files').checked || false;
        const allFolderFiles = scheduleModal.querySelector('#all-folder-files').checked || false;

        const type = scheduleModal.getAttribute('data-type');

        if (type === 'crowdin') {
            const crowdinFiles = await appComponent.current.getCrowdinFilesData();
            const syncedFileIds = Object.keys(syncData.current);
            const crowdinFolderIds = crowdinFiles
                .filter((file: any) => file.node_type === folderType || file.node_type === branchType)
                .filter((file: any) => syncedFileIds.includes(String(file.id)))
                .map((file: any) => String(file.id));

            appComponent.current.setCrowdinScheduleSync(syncData.current, newFile, selectedFiles);
            appComponent.current.setCrowdinFilesHasSyncOption(true);
            const syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
            appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
            updateSyncSettings(syncedFiles, 'schedule', 'crowdin', [], crowdinFolderIds);
        } else if (type === 'integration') {
            if (syncNewElements?.integration) {
                appComponent.current.setIntegrationScheduleSync(syncData.current, newFile, selectedFiles);
            } else {
                appComponent.current.setIntegrationScheduleSync(syncData.current);
            }

            appComponent.current.setIntegrationFilesHasSyncOption(true);
            const syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
            appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

            if (integrationOneLevelFetching) {
                updateSyncSettings(
                    syncedFiles,
                    'schedule',
                    'integration',
                    selectedFiles ? getIntegrationFoldersToExpand(syncData.current, syncedFiles) : [],
                );
            } else {
                if (allFolderFiles) {
                    const integrationExpandedFolders = syncData.current
                        .filter((file: any) => file.node_type === folderType)
                        .map((file: any) => ({
                            ...file,
                            nodeType: file.node_type,
                            parentId: file.parent_id,
                        }));
                    updateSyncSettings(syncedFiles, 'schedule', 'integration', integrationExpandedFolders);
                } else {
                    updateSyncSettings(syncedFiles, 'schedule', 'integration');
                }
            }
        }

        closeModal(scheduleModal);
    }

    async function openScheduleModal(type: any, options = {} as any) {
        const scheduleModal = document.getElementById('confirm-schedule-modal') as any;

        const { showAllFolderFiles = false, showNewFiles = true } = options;

        const newFile = scheduleModal.querySelector('#new-files');
        const selectedFiles = scheduleModal.querySelector('#selected-files');
        const allFolderFiles = scheduleModal.querySelector('#all-folder-files');
        let newFileStatus = 'unChecked';

        const integrationSyncFolders = (
            (await (document.querySelector('crowdin-simple-integration') as any)?.getIntegrationScheduleSync(true)) ||
            []
        ).filter((elements: any) => elements.node_type === '0');

        const selectedIntegrationFolders = (
            (await (document.getElementById('integration-files') as any)?.getSelected(true)) || []
        ).filter((elements: any) => elements.node_type === '0');

        for (let i = 0; i < selectedIntegrationFolders.length; i++) {
            const selectedElement = selectedIntegrationFolders[i];

            const found = integrationSyncFolders.some(
                (integrationElement: any) => integrationElement.id === selectedElement.id,
            );

            if (found) {
                newFileStatus = 'checked';
            } else if (!found && newFileStatus === 'checked') {
                newFileStatus = 'clear-selection';
                break;
            } else {
                newFileStatus = 'unChecked';
            }
        }

        if (showNewFiles) {
            newFile.removeAttribute('hidden');
            if (newFileStatus === 'checked') {
                newFile[newFileStatus] = true;
                newFile.value = true;
                newFile.removeAttribute('clear-selection');
            } else if (newFileStatus === 'clear-selection') {
                newFile.checked = false;
                newFile.value = false;
                newFile.setAttribute(newFileStatus, '');
            } else {
                newFile.checked = false;
                newFile.value = false;
                newFile.removeAttribute('clear-selection');
            }
        } else {
            newFile.setAttribute('hidden', '');
            newFile.checked = false;
        }

        if (showAllFolderFiles) {
            allFolderFiles.removeAttribute('hidden');
            allFolderFiles.checked = false;
            allFolderFiles.value = false;
        } else {
            allFolderFiles.setAttribute('hidden', '');
            allFolderFiles.checked = false;
        }

        selectedFiles.checked = true;
        selectedFiles.value = true;
        scheduleModal.querySelector('#save-schedule-sync').setAttribute('disabled', false);
        scheduleModal.setAttribute('data-type', type);
        openModal(scheduleModal);
    }

    function onChangeAutoSynchronizationOptions() {
        const newFiles = document.getElementById('new-files') as any;
        newFiles.removeAttribute('clear-selection');

        const newFilesStatus = newFiles.checked || false;
        const selectedFiles = (document.getElementById('selected-files') as any).checked || false;
        const allFolderFiles = (document.getElementById('all-folder-files') as any).checked || false;
        const buttonSaveScheduleSync = document.getElementById('save-schedule-sync') as any;

        if (newFilesStatus || selectedFiles || allFolderFiles) {
            buttonSaveScheduleSync.removeAttribute('disabled');
        } else {
            buttonSaveScheduleSync.setAttribute('disabled', true);
        }
    }

    async function hasFolder(selectedFiles: any) {
        let isFolder;
        if (Array.isArray(selectedFiles)) {
            isFolder = selectedFiles.find((file) => file.node_type === folderType || file.node_type === branchType);
        } else {
            const files = await appComponent.current.getCrowdinFilesData();
            const folders = files.filter((file: any) => file.node_type === folderType || file.node_type === branchType);
            isFolder = folders.find((folder: any) => selectedFiles.hasOwnProperty(folder.id));
        }

        return isFolder !== undefined;
    }

    async function setIntegrationScheduleSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        let syncedFiles;

        if (syncNewElements?.integration) {
            const newIntegrationFiles = config.current?.['new-integration-files'];

            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);

            const shouldShowModal = isFolder && !newIntegrationFiles;
            const showNewFiles = true; // завжди показуємо New files коли є syncNewElements.integration
            const showAllFolderFiles = hasLoadMoreElements.current; // показуємо All files якщо є hasLoadMoreElements

            if (shouldShowModal) {
                await openScheduleModal('integration', { showNewFiles, showAllFolderFiles });
                return;
            }

            appComponent.current.setIntegrationScheduleSync(e.detail);
            appComponent.current.setIntegrationFilesHasSyncOption(true);
            syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
        } else {
            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);

            if (hasLoadMoreElements.current && isFolder) {
                await openScheduleModal('integration', { showNewFiles: false, showAllFolderFiles: true });
                return;
            }

            syncedFiles = await appComponent.current.getIntegrationScheduleSync();
        }

        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

        if (integrationOneLevelFetching) {
            updateSyncSettings(
                syncedFiles,
                'schedule',
                'integration',
                getIntegrationFoldersToExpand(e.detail, syncedFiles),
            );
        } else {
            if (hasLoadMoreElements.current) {
                const integrationExpandedFolders = e.detail
                    .filter((file: any) => file.node_type === folderType)
                    .map((file: any) => ({
                        ...file,
                        nodeType: file.node_type,
                        parentId: file.parent_id,
                    }));

                updateSyncSettings(syncedFiles, 'schedule', 'integration', integrationExpandedFolders);
            } else {
                updateSyncSettings(syncedFiles, 'schedule', 'integration');
            }
        }
    }

    async function setCrowdinScheduleSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        let syncedFiles;

        if (syncNewElements?.crowdin) {
            const newCrowdinFiles = config.current?.['new-crowdin-files'];

            syncData.current = e.detail;
            const isFolder = await hasFolder(e.detail);
            if (isFolder && !newCrowdinFiles) {
                await openScheduleModal('crowdin');
                return;
            }

            appComponent.current.setCrowdinScheduleSync(e.detail);
            appComponent.current.setCrowdinFilesHasSyncOption(true);
            syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync();
        }

        appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);
        updateSyncSettings(syncedFiles, 'schedule', 'crowdin');
    }

    async function disableIntegrationSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        appComponent.current.setAttribute('is-integration-sync-settings-in-progress', true);

        let syncedFiles;
        if (syncNewElements?.integration) {
            syncedFiles = await appComponent.current.getIntegrationScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getIntegrationScheduleSync();
        }

        updateSyncSettings(syncedFiles, 'schedule', 'integration');
    }

    async function disableCrowdinSync(e: any) {
        if (e.detail.length === 0) {
            showToast('Select templates which will be pushed to Crowdin');
            return;
        }

        appComponent.current.setAttribute('is-crowdin-sync-settings-in-progress', true);

        let syncedFiles;
        if (syncNewElements?.crowdin) {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync(true);
        } else {
            syncedFiles = await appComponent.current.getCrowdinScheduleSync();
        }

        updateSyncSettings(syncedFiles, 'schedule', 'crowdin');
    }

    function updateSyncSettings(
        files: any,
        type: string,
        provider: string,
        expandIntegrationFolders = [] as any[],
        folderIds = [] as string[],
    ) {
        checkOrigin()
            .then((restParams) =>
                fetch('api/sync-settings' + restParams, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        files,
                        type,
                        provider,
                        expandIntegrationFolders,
                        hasLoadMoreElements: hasLoadMoreElements.current,
                        folderIds,
                    }),
                }),
            )
            .then(checkResponse)
            .then((res) => {
                checkJob({
                    jobId: res?.jobId,
                    jobType: (JOB_TYPE as any)[`${provider}SyncSettingsSave`],
                });
            })
            .catch((e) => catchRejection(e, "Can't save schedule sync settings"));
    }

    function getSubscriptionInfo() {
        const subscriptionInfo = document.getElementById('subscription-info') as any;
        const subscriptionInfoText = subscriptionInfo.getElementsByTagName('p')[0];
        checkOrigin()
            .then((restParams) => fetch('api/subscription-info' + restParams))
            .then(checkResponse)
            .then((res) => {
                if (res.showInfo) {
                    subscriptionLink.current = res.subscribeLink;
                    subscriptionInfoText.textContent = `Your trial expires in ${res.daysLeft} days.`;
                    subscriptionInfo.style.display = 'block';
                }
            });
    }

    function checkAlert(alert: any, suffix: string) {
        const name = suffix ?? '${name}';
        const revised = localStorage.getItem(`revised_${name}`) === '1';
        if (!revised) {
            alert.style.display = 'block';
        }
    }

    function closeAlert(el: any, suffix: string) {
        const name = suffix ?? '${name}';
        const alert = el.closest('crowdin-alert');
        alert.style.display = 'none';
        localStorage.setItem(`revised_${name}`, 1 as any);
    }

    function showErrorLogs() {
        (document.getElementById('show-error-logs-btn') as any).classList.add('hidden');
        (document.getElementById('show-integration-btn') as any).classList.remove('hidden');

        appComponent.current.classList.add('hidden');
        (document.getElementById('user-errors') as any).classList.remove('hidden');

        checkOrigin()
            .then((restParams) => fetch('api/user-errors' + restParams))
            .then(checkResponse)
            .then((res) => {
                const table = document.getElementById('user-errors-table') as any;

                const clickRow = (field: any, index: any, item: any) => {
                    const modal = document.getElementById('user-error-detail') as any;
                    openModal(modal);
                    modal.innerHTML = getUserErrorDetail(item);
                };

                const formatDate = (field: any, index: any, item: any) => {
                    return item.formattedDate;
                };

                if (table.setTableConfig) {
                    table.setTableConfig({
                        defaultSortingColumn: 'createdAt',
                        defaultSortingDir: 'desc',
                        headerFields: [
                            {
                                field: 'createdAt',
                                name: 'Date',
                                isSortable: true,
                                clickFn: clickRow,
                                formatFn: formatDate,
                            },
                            {
                                field: 'action',
                                name: 'Action',
                                clickFn: clickRow,
                            },
                            {
                                field: 'message',
                                name: 'Message',
                                clickFn: clickRow,
                            },
                        ],
                    });
                }

                table.setTableData(JSON.stringify(res));
                table.setAttribute('is-loading', false);
            })
            .catch((e) => catchRejection(e, "Can't fetch error logs"));
    }

    function showIntegration() {
        (document.getElementById('show-error-logs-btn') as any).classList.remove('hidden');
        (document.getElementById('show-integration-btn') as any).classList.add('hidden');

        appComponent.current.classList.remove('hidden');
        (document.getElementById('user-errors') as any).classList.add('hidden');
    }

    function isJSON(string: string) {
        try {
            JSON.parse(string);
            return true;
        } catch (e) {
            return false;
        }
    }

    function getUserErrorDetail(error: any) {
        const data = JSON.parse(error.data);

        let html = '<div class="error-detail-table"><table>';
        html += `<tr><td>Action</td><td>${sanitizeHTML(error.action)}</td></tr>`;
        if (isJSON(error.message)) {
            html += `<tr><td>Message</td><td><pre>${sanitizeHTML(error.message)}<pre></td></tr>`;
        } else {
            html += `<tr><td>Message</td><td>${sanitizeHTML(error.message)}</td></tr>`;
        }
        html += `<tr><td>Date/time</td><td>${sanitizeHTML(error.formattedDate)}</td></tr>`;

        if (data.requestParams) {
            html += `<tr><td>Method</td><td>${sanitizeHTML(data.requestParams.method)}</td></tr>`;
        }

        if (data.responseData) {
            html += `<tr><td>Response Data</td><td><pre>${sanitizeHTML(JSON.stringify(data.responseData, null, 2))}</pre></td></tr>`;
        }

        if (data.appData) {
            if (Array.isArray(data.appData)) {
                html += `<tr><td>App Data</td><td><pre>${sanitizeHTML(JSON.stringify(data.appData, null, 2))}</pre></td></tr>`;
            } else if (typeof data.appData === 'object') {
                for (const key in data.appData) {
                    html += `<tr><td>${sanitizeHTML(key.charAt(0).toUpperCase() + key.slice(1))}</td><td><pre>${sanitizeHTML(JSON.stringify(data.appData[key], null, 2))}</pre></td></tr>`;
                }
            } else {
                html += `<tr><td>App Data</td><td>${sanitizeHTML(data.appData)}</td></tr>`;
            }
        }

        html += '</table></div>';

        return html;
    }

    function sanitizeHTML(str: string) {
        const tempDiv = document.createElement('div');
        tempDiv.textContent = str;
        return tempDiv.innerHTML;
    }

    function openModal(modal: any) {
        modal.style.display = 'block';
        modal.open();
    }

    function closeModal(modal: any) {
        modal.style.display = 'none';
        modal.close();
    }

    function settingsFieldChange(el: any) {
        el.removeAttribute('error');
        el.removeAttribute('error-text');
    }

    function isFolder(e: any) {
        return !e.type && (e.nodeType === undefined || e.nodeType === folderType || e.nodeType === branchType);
    }

    return (
        <div>
            <div className="i_w">
                <div className="top">
                    {notice && (
                        <crowdin-alert
                            id="notice"
                            title={notice.title}
                            {...(!notice.icon ? { 'no-icon': 'true' } : {})}
                            {...(notice.type ? { type: notice.type } : {})}
                            style={{ display: 'none', marginBottom: '12px' }}
                        >
                            <div className="box-center">
                                <p className="m-0" dangerouslySetInnerHTML={{ __html: notice.content }} />
                            </div>
                            {notice.close && (
                                <crowdin-button
                                    onClick={(e: any) => closeAlert(e, 'notice')}
                                    class="dismiss-alert"
                                    icon
                                >
                                    close
                                </crowdin-button>
                            )}
                        </crowdin-alert>
                    )}

                    {checkSubscription && (
                        <crowdin-alert
                            id="subscription-info"
                            no-icon="true"
                            type="warning"
                            style={{ display: 'none', marginBottom: '12px' }}
                        >
                            <div className="box-center">
                                <p className="m-0"></p>
                                <crowdin-button
                                    class="ml-1"
                                    primary
                                    onClick={() => window.open(subscriptionLink.current, '_blank')}
                                >
                                    Subscribe
                                </crowdin-button>
                            </div>
                        </crowdin-alert>
                    )}

                    <div id="buttons">
                        <crowdin-button
                            id="show-integration-btn"
                            class="hidden"
                            icon-before="arrow_back"
                            onClick={() => showIntegration()}
                        >
                            Integration
                        </crowdin-button>
                        <crowdin-button id="show-error-logs-btn" icon-before="list" onClick={() => showErrorLogs()}>
                            Error logs
                        </crowdin-button>
                        {isManager && (
                            <crowdin-button icon-before="link" onClick={() => showPermissionsDialog()}>
                                Share
                            </crowdin-button>
                        )}
                        {infoModal && (
                            <crowdin-button
                                icon-before="info"
                                onClick={() => {
                                    const infoModalEl = document.getElementById('info-modal');
                                    openModal(infoModalEl);
                                }}
                            >
                                {infoModal.title}
                            </crowdin-button>
                        )}
                        {configurationFields && (
                            <crowdin-button
                                icon-before="settings"
                                onClick={() => {
                                    const settingsModal = document.getElementById('settings-modal');
                                    openModal(settingsModal);
                                    fillSettingsForm();
                                }}
                            >
                                Settings
                            </crowdin-button>
                        )}
                        <crowdin-button
                            {...(!isOwner ? { disabled: true, title: 'Only the owner can log out' } : {})}
                            icon-before="account_circle"
                            onClick={() => integrationLogout()}
                        >
                            Log out
                        </crowdin-button>
                    </div>
                </div>

                <crowdin-simple-integration
                    ref={appComponent}
                    async-progress
                    {...(syncNewElements?.crowdin ? { 'skip-crowdin-auto-schedule': true } : {})}
                    {...(syncNewElements?.integration ? { 'skip-integration-auto-schedule': true } : {})}
                    {...(withCronSync?.crowdin || webhooks?.crowdin ? { 'crowdin-schedule': 'true' } : {})}
                    {...(withCronSync?.integration || webhooks?.integration ? { 'integration-schedule': 'true' } : {})}
                    {...(integrationOneLevelFetching ? { 'integration-one-level-fetching': 'true' } : {})}
                    {...(integrationSearchListener ? { 'allow-integration-filter-change-listener': 'true' } : {})}
                    {...(integrationPagination ? { 'integration-load-more-files': 'true' } : {})}
                    integration-name={name}
                    integration-logo="logo.png"
                    {...((integrationButtonMenuItems?.length ?? 0) > 0
                        ? { 'integration-button-menu-items': integrationButtonMenuItems }
                        : {})}
                    {...((crowdinButtonMenuItems?.length ?? 0) > 0
                        ? { 'crowdin-button-menu-items': crowdinButtonMenuItems }
                        : {})}
                    {...(filtering?.crowdinLanguages ? { 'crowdin-filter': true } : {})}
                    {...(filtering?.integrationFilterConfig
                        ? {
                              'integration-filter': true,
                              'integration-filter-config': filtering.integrationFilterConfig,
                          }
                        : {})}
                ></crowdin-simple-integration>

                <div id="user-errors" className="hidden">
                    <crowdin-show-as-table
                        is-loading
                        id="user-errors-table"
                        is-searchable
                        total-records="25"
                        search-placeholder="Search something"
                    ></crowdin-show-as-table>
                    <crowdin-alert>
                        This table displays the most recent error logs from the past month. Logs older than one month
                        will be automatically deleted.
                    </crowdin-alert>
                </div>
            </div>

            <crowdin-toasts></crowdin-toasts>
            <crowdin-async-progress cancelAsyncAction=""></crowdin-async-progress>

            {/* Subscription Modal */}
            <crowdin-modal
                ref={subscriptionModal}
                style={{ display: 'none' }}
                id="subscription-modal"
                modal-width="50"
                close-button="false"
            >
                <div>
                    <crowdin-alert type="warning">Subscribe to continue using the {name} app.</crowdin-alert>
                </div>
                <div slot="footer">
                    <crowdin-button primary onClick={() => window.open(subscriptionLink.current, '_blank')}>
                        Subscribe
                    </crowdin-button>
                    <crowdin-button outlined onClick={() => window.open('https://crowdin.com/contacts', '_blank')}>
                        Contact us
                    </crowdin-button>
                    <crowdin-button class="ml-10" secondary onClick={() => integrationLogout()}>
                        Log out
                    </crowdin-button>
                </div>
            </crowdin-modal>

            {/* Permissions Modal */}
            <crowdin-modal
                style={{ display: 'none' }}
                id="permissions-modal"
                modal-title="Share"
                close-button-title="Close"
                close-button
                body-overflow-unset
            >
                <div className="loader hidden">
                    <crowdin-progress-indicator></crowdin-progress-indicator>
                </div>
                <div className="permissions-modal-content">
                    <div className="select-users-block">
                        <crowdin-users-select
                            ref={usersSelectRef}
                            allow-new-options
                            is-multi
                            is-searchable
                            id="users"
                            key="users"
                            label="Users"
                            help-text="Search for members by name, username, email, or invite new ones using their email."
                            is-position-fixed
                        ></crowdin-users-select>
                    </div>
                    <div className="select-users-info">
                        <div className="confirm-users-block mt-2 hidden">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '40%' }}>Access</th>
                                        <th style={{ width: '35%' }}>Users</th>
                                        <th className="status" style={{ width: '20%' }}>
                                            Upcoming changes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hasOrganization && (
                                        <tr className="organization-invite">
                                            <td>
                                                <crowdin-p>Registration in Organization</crowdin-p>
                                                <div className="permission-description"></div>
                                            </td>
                                            <td className="affected-users"></td>
                                            <td className="status"></td>
                                        </tr>
                                    )}
                                    <tr className="project-invite">
                                        <td>
                                            <crowdin-p>Project Manager Access</crowdin-p>
                                        </td>
                                        <td className="affected-users"></td>
                                        <td className="status"></td>
                                    </tr>
                                    <tr className="application-settings-invite">
                                        <td>
                                            <crowdin-p>Application Visibility Access</crowdin-p>
                                            <div className="permission-description"></div>
                                        </td>
                                        <td className="affected-users"></td>
                                        <td className="status"></td>
                                    </tr>
                                    <tr className="application-credentials-invite">
                                        <td>
                                            <crowdin-p>Access to the Integration</crowdin-p>
                                            <div className="permission-description">
                                                This provides access to sync files and modify the current integration
                                                settings.
                                            </div>
                                        </td>
                                        <td className="affected-users"></td>
                                        <td className="status"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {zenModeUrl && (
                            <div className="mt-2">
                                <crowdin-input
                                    with-fixed-height
                                    label="Zen Mode link"
                                    help-text="This focused view allows you to concentrate solely on the Integrations section, eliminating other distractions."
                                    value={zenModeUrl}
                                    name="zenModeLink"
                                    with-copy-button
                                ></crowdin-input>
                            </div>
                        )}
                    </div>
                </div>
                <div slot="footer">
                    <crowdin-button id="confirm-users-btn" outlined onClick={() => inviteUsers()}>
                        Save
                    </crowdin-button>
                </div>
            </crowdin-modal>

            {/* Excluded Languages Modal */}
            {excludedTargetLanguages && (
                <crowdin-modal
                    ref={languageModal}
                    modal-width="50"
                    modal-title="Select Target Languages for Translation"
                    id="excluded-languages"
                ></crowdin-modal>
            )}

            {/* Info Modal */}
            {infoModal && (
                <crowdin-modal
                    style={{ display: 'none' }}
                    id="info-modal"
                    modal-width="50"
                    modal-title={infoModal.title}
                    close-button-title="Close"
                >
                    <div dangerouslySetInnerHTML={{ __html: infoModal.content }} />
                </crowdin-modal>
            )}

            {/* Settings Modal */}
            {configurationFields && (
                <crowdin-modal
                    style={{ display: 'none' }}
                    id="settings-modal"
                    modal-width="65"
                    modal-title="Settings"
                    close-button-title="Close"
                >
                    <div className="loader hidden">
                        <crowdin-progress-indicator></crowdin-progress-indicator>
                    </div>
                    <div id="modal-content">
                        <crowdin-tabs
                            tabs-config={JSON.stringify(configurationFields.map((f) => f.name))}
                            active-tab="1"
                        >
                            {configurationFields.map((tab, tabIndex) => (
                                <div key={tabIndex} slot={`tab-${tabIndex + 1}`}>
                                    <div className="crowdin-form">
                                        {tab.fields.map((field) => (
                                            <Fragment key={field.key}>
                                                {renderConfigField(field, settingsFieldChange)}
                                                <div
                                                    style={{ padding: '8px' }}
                                                    {...(field.dependencySettings
                                                        ? { 'data-dependency': field.dependencySettings }
                                                        : {})}
                                                ></div>
                                            </Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </crowdin-tabs>
                    </div>
                    <div slot="footer">
                        <crowdin-button id="settings-save-btn" outlined onClick={() => saveSettings()}>
                            Save
                        </crowdin-button>
                    </div>
                </crowdin-modal>
            )}

            {/* Confirm Schedule Modal */}
            {(syncNewElements?.crowdin || syncNewElements?.integration) && (
                <crowdin-modal
                    style={{ display: 'none' }}
                    id="confirm-schedule-modal"
                    modal-width="50"
                    modal-title="Synchronization options"
                    close-button-title="Close"
                    close-button="true"
                    body-overflow-unset
                >
                    <crowdin-checkbox
                        id="selected-files"
                        name="selected-files"
                        label="Selected files"
                        class="hydrated"
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <crowdin-checkbox
                        id="all-folder-files"
                        name="all-folder-files"
                        label="All files in the folder"
                        class="hydrated"
                        hidden
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <crowdin-checkbox
                        id="new-files"
                        name="new-files"
                        label="New files"
                        class="hydrated"
                        onChange={() => onChangeAutoSynchronizationOptions()}
                    ></crowdin-checkbox>
                    <div slot="footer">
                        <crowdin-button outlined id="save-schedule-sync" onClick={() => saveScheduleSync()}>
                            Save
                        </crowdin-button>
                    </div>
                </crowdin-modal>
            )}

            {/* User Error Detail Modal */}
            <crowdin-modal
                style={{ display: 'none' }}
                id="user-error-detail"
                close-button-title="Close"
                close-button="true"
            ></crowdin-modal>
        </div>
    );
};
